import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { FileUploadZone } from './FileUploadZone';
import { FilePreviewDialog } from './FilePreviewDialog';
import { format } from 'date-fns';
import { useUserRoles } from '@/hooks/useUserRoles';

interface FundraisingFolderViewProps {
  companyId: string;
}

const SUBFOLDERS = [
  'Pitch Deck',
  'Financial Model',
  'SAFE Docs',
  'Valuation Model',
  'Legal',
  'Traction',
];

export default function FundraisingFolderView({ companyId }: FundraisingFolderViewProps) {
  const { isOwner, isAdmin } = useUserRoles(companyId);
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [subfolder, setSubfolder] = useState('Pitch Deck');
  const [visibility, setVisibility] = useState('INTERNAL');
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  async function loadFiles() {
    setLoading(true);
    const { data, error } = await supabase
      .from('data_room_files')
      .select('*')
      .eq('company_id', companyId)
      .eq('category', 'Fundraising')
      .order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    setFiles(data || []);
    setLoading(false);
  }

  async function handleFilesSelected(selectedFiles: File[]) {
    if (!companyId || selectedFiles.length === 0) return;

    // Validate visibility permissions
    const isOwnerOrAdmin = isOwner(companyId) || isAdmin(companyId);
    if (!isOwnerOrAdmin && visibility !== 'INTERNAL') {
      toast.error(
        'Only owners and admins can upload files with Board, Investor, or Public visibility'
      );
      return;
    }

    setUploading(true);
    try {
      let successCount = 0;

      for (const file of selectedFiles) {
        const path = `${companyId}/fundraising/${subfolder}/${Date.now()}-${Math.random().toString(36).substring(7)}-${file.name}`;

        const { error: upErr } = await supabase.storage.from('data-room').upload(path, file);

        if (upErr) {
          console.error(`Failed to upload ${file.name}:`, upErr);
          continue;
        }

        const { error } = await supabase.from('data_room_files').insert({
          company_id: companyId,
          file_name: file.name,
          file_url: path,
          category: 'Fundraising',
          folder: subfolder,
          visibility: visibility,
          size_bytes: file.size,
          mime_type: file.type,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        });

        if (error) {
          console.error(`Failed to save metadata for ${file.name}:`, error);
          continue;
        }

        // Trigger Ella fundraising indexer (non-blocking)
        supabase.functions
          .invoke('ella-fundraising-indexer', {
            body: { companyId, filePath: path },
          })
          .catch((err) => console.error('Indexer error:', err));

        successCount++;
      }

      if (successCount > 0) {
        toast.success(
          `Successfully uploaded ${successCount} of ${selectedFiles.length} files and queued for AI indexing`
        );
        loadFiles();
      } else {
        toast.error('No files were uploaded successfully');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    loadFiles();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <CardTitle>Fundraising Folder</CardTitle>
        </div>
        <Button variant="outline" size="sm" onClick={loadFiles}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Subfolder</label>
            <Select value={subfolder} onValueChange={setSubfolder}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUBFOLDERS.map((f) => (
                  <SelectItem key={f} value={f}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Visibility</label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INTERNAL">Internal - Owners & Admins only</SelectItem>
                <SelectItem value="BOARD">Board - Owners, Admins & Board Observers</SelectItem>
                <SelectItem value="INVESTOR">Investor - Includes all Investors</SelectItem>
                <SelectItem value="PUBLIC">Public - All company members</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <FileUploadZone
          onFilesSelected={handleFilesSelected}
          multiple={true}
          disabled={uploading}
          maxSize={50}
        />

        {loading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : files.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subfolder</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <Badge variant="outline">{f.folder || '—'}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{f.file_name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{f.visibility || 'INTERNAL'}</Badge>
                  </TableCell>
                  <TableCell>
                    {f.size_bytes ? `${(f.size_bytes / 1024).toFixed(1)} KB` : '—'}
                  </TableCell>
                  <TableCell>{format(new Date(f.created_at), 'PP')}</TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPreviewFile(f);
                        setPreviewOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-8">
            No fundraising documents yet. Upload pitch decks, financial models, or SAFE docs to get
            started.
          </p>
        )}
      </CardContent>

      <FilePreviewDialog
        file={previewFile}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        bucketName="data-room"
      />
    </Card>
  );
}
